import { grazeValueMul } from '../bombs';
import { DT, ULTI_FILL_GRAZE } from '../constants';
import { clampDifficulty, GRAZE_SCORES } from '../difficulty';
import { Motion } from '../defs';
import { DRONE_DOWN_TIME, DRONE_RADIUS, DRONE_SLOTS, FEEL, HULL_GUNS } from '../FeelConstants';
import { Ev } from '../events';
import { cos } from '../math';
import { Fam, PB_PERSISTENT, PbMode } from '../pools/BulletPool';
import { BEAM_PULSE } from '../secondary';
import { shatterObstacle } from './obstacleSystem';
import type { World } from '../World';

/** Points marqués par un frôlement. Extrait de la boucle pour que la majoration
 *  de la bombe TEMPS MORT porte sur une valeur NOMMÉE plutôt que sur un 25
 *  perdu au milieu d'une condition. */
const GRAZE_SCORE = 25;

/* ── Bouclier joueur : réglages de l'absorption ──────────────────────────────
   Ces trois-là gouvernent ce qui se passe quand une barrière encaisse un coup.
   Ils vivent ICI, avec la logique d'absorption (`hitPlayer`) ; la régénération,
   elle, lit la stat `shieldRegen` dans `playerSystem`. */

/** Coût d'un coup encaissé, en charges de bouclier. Un coup = une charge : la
 *  capacité (`maxShield`) se lit alors directement comme « nombre de coups
 *  encaissables », ce qui rend les cartes de `bouclier.def.ts` immédiatement
 *  compréhensibles (« +1 charge » = un coup de plus). */
const SHIELD_HIT_COST = 1;

/** Invulnérabilité accordée quand le bouclier casse, en secondes. COURTE — bien
 *  en deçà des 1,4 s d'une réapparition. Elle a un seul rôle : empêcher qu'un
 *  amas dense ne vide tout le bouclier dans le même souffle, et laisser le temps
 *  de sortir d'un corps ennemi au contact. Trop longue, elle ferait du bouclier
 *  une invulnérabilité déguisée. */
const SHIELD_BREAK_IFRAMES = 0.5;

/** Délai SANS coup avant que la régénération ne reprenne, en secondes. Combiné à
 *  `shieldRegen` (0,6/s), une charge revient en ~4,7 s : assez lent pour que le
 *  bouclier reste une bouée pour un mauvais moment, jamais une barrière qu'on
 *  spamme. C'est le garde-fou central du « ne doit PAS être trivialement
 *  invincible ». */
const SHIELD_REGEN_DELAY = 3;

/** Décroissance des dégâts d'un tir PRIMAIRE persistant (perforant, ricochet) à
 *  CHAQUE ennemi traversé — le garde-fou multi-cible.
 *
 *  ⚠️ C'est le cœur de l'équilibrage de ces armes. Une balle qui perfore ou
 *  ricoche touche N ennemis ; sans malus, son DPS EFFECTIF grimperait linéairement
 *  avec le nombre de cibles, exactement le power creep que le jeu interdit — une
 *  arme conçue à 200 DPS nominaux en délivrerait 600 sur une file de trois. La
 *  puissance décroît donc géométriquement : une file de trois reçoit
 *  1 + 0,6 + 0,36 = 1,96× le mono-cible, jamais 3×. Le seuil du test
 *  `tests/balance/multicible.test.ts` (≤ 2,2×) borne cette amplification et ferait
 *  échouer une perforation non bornée — c'est son rôle.
 *
 *  ⚠️ RÉSERVÉ au tir PRIMAIRE (`Fam.Player`/`Fam.PlayerDrone`) : les armes
 *  SECONDAIRES à conduite (Scie = Pierce, Orage = Arc, `Fam.PlayerSecondary`) sont
 *  déjà bornées par leur budget de contacts et par `validateSecondaries`. Les
 *  nerfer casserait `tests/sim/arsenal.test.ts`, qui vérifie qu'un disque inflige
 *  EXACTEMENT ses dégâts par corps traversé. */
const PRIMARY_PIERCE_FALLOFF = 0.6;

/**
 * Distance minimale au carré entre deux disques pendant un tick, chacun se
 * déplaçant en ligne droite de (0) vers (1).
 *
 * Pourquoi ce n'est pas un luxe (PLAN.md §3.4) : à 1 500 u/s et 120 Hz, une
 * balle parcourt 12,5 u par pas alors que la hitbox du joueur fait 9 u de
 * rayon. Un test ponctuel sur la position d'arrivée la laisserait donc
 * TRAVERSER le joueur sans jamais le toucher — un tunnel, et le pire bug
 * possible dans ce genre : invisible en développement à basse densité,
 * systématique chez les joueurs sur les patterns rapides.
 *
 * On travaille dans le référentiel relatif : le problème se ramène au point le
 * plus proche de l'origine sur un segment, soit une projection scalaire bornée
 * à [0, 1]. Aucune racine carrée n'est nécessaire.
 */
export function sweptDist2(
  ax0: number,
  ay0: number,
  ax1: number,
  ay1: number,
  bx0: number,
  by0: number,
  bx1: number,
  by1: number,
): number {
  const fx = ax0 - bx0;
  const fy = ay0 - by0;
  const rx = ax1 - ax0 - (bx1 - bx0);
  const ry = ay1 - ay0 - (by1 - by0);

  const a = rx * rx + ry * ry;
  if (a < 1e-12) return fx * fx + fy * fy; // pas de mouvement relatif

  let t = -(fx * rx + fy * ry) / a;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;

  const cx = fx + rx * t;
  const cy = fy + ry * t;
  return cx * cx + cy * cy;
}

/** Applique un recul à un ennemi touché, dans la direction du projectile.
 *
 *  ⚠️ Seuls les PORTEURS reculent (`parent < 0`). Une partie est repositionnée
 *  sur son porteur à chaque tick (`motionSystem`) : lui donner un recul propre
 *  serait écrasé aussitôt, et un bras boulonné à un boss de plusieurs tonnes
 *  n'a de toute façon aucune raison de tressaillir.
 *
 *  L'intensité décroît avec les PV MAXIMAUX — la piétaille bondit, un lourd
 *  bronche, un boss ne bouge pas — et le décalage cumulé est plafonné pour
 *  qu'un ennemi mitraillé ne sorte pas du cadre. */
function knockback(w: World, e: number, vx: number, vy: number, dmg: number): void {
  const en = w.enemies;
  if (en.parent[e]! >= 0) return;
  /* ⚠️ Les ancrés ne reculent PAS, quelle que soit leur masse. Le Mortier
     (Motion.Anchor) existe pour basculer d'« adversaire » à « décor » dans
     l'œil du joueur : il devient du terrain tactique fixe. Le voir tressaillir
     sous le tir le ferait redevenir une cible, et toute sa raison d'être — être
     un obstacle qu'on contourne, pas qu'on bouscule — disparaîtrait. C'est une
     décision d'IDENTITÉ, pas d'équilibrage : le test du Mortier l'a fait
     ressortir. */
  const def = w.defs.enemies[en.kind[e]!];
  if (def && def.motion.kind === Motion.Anchor) return;
  const sp = Math.sqrt(vx * vx + vy * vy);
  if (sp < 1e-3) return;
  const push = (FEEL.kbStrength * dmg) / (en.maxHp[e]! > 1 ? en.maxHp[e]! : 1);
  const ox = en.kbx[e]!;
  const oy = en.kby[e]!;
  let kx = ox + (vx / sp) * push;
  let ky = oy + (vy / sp) * push;
  const mag = Math.sqrt(kx * kx + ky * ky);
  if (mag > FEEL.kbMaxOffset) {
    const f = FEEL.kbMaxOffset / mag;
    kx *= f;
    ky *= f;
  }
  en.kbx[e] = kx;
  en.kby[e] = ky;
  /* ⚠️ On répercute le changement sur la position VISIBLE dans le même geste.
     `motionSystem` maintient l'invariant `position = trajectoire + recul` ;
     modifier `kbx`/`kby` sans toucher `x`/`y` le romprait jusqu'au prochain
     tour de `motionSystem`, qui retirerait alors un recul jamais ajouté et
     ferait discrètement RECULER l'ennemi dans le mauvais sens. Le test de
     « retour à la trajectoire » l'a attrapé. */
  const cur = en.x[e]!;
  en.x[e] = cur + (kx - ox);
  const cury = en.y[e]!;
  en.y[e] = cury + (ky - oy);
}

export function collisionSystem(w: World): void {
  const p = w.player;

  /* ═══ 1. projectiles joueur → ennemis ════════════════════════════════════
     Balayé, comme les tirs ennemis.

     La version précédente faisait un test ponctuel, justifié par un invariant
     comparant le déplacement du tir au plus petit rayon de hurtbox. Cet
     invariant était faux sur deux points : il ignorait la vitesse de FERMETURE
     (l'ennemi bouge lui aussi — jusqu'à 720 u/s pour un Dard en piqué), et il
     ne disait rien des impacts rasants, où la fenêtre de recouvrement tend vers
     zéro quelle que soit la marge. Balayer supprime le problème au lieu de le
     borner, pour une dizaine d'opérations par paire — sur un budget mesuré à
     0,337 ms des 0,900 alloués.

     Force brute assumée : ~500 tirs × ~200 ennemis restent sous le seuil où une
     structure spatiale devient rentable (PLAN.md §1.1) — construire une grille
     coûterait plus cher que les comparaisons qu'elle éviterait. */
  const pb = w.pb;
  const en = w.enemies;
  for (let i = pb.count - 1; i >= 0; i--) {
    const s = pb.at(i);
    /* ⚠️ Cette boucle LIBÈRE à la première touche — c'est le « pas de
       perforation au J1 » sur lequel se termine son corps. Les projectiles à
       conduite persistante (disque, arc, rayon) existent précisément pour
       survivre au contact : les laisser entrer ici les tuerait avant que la
       passe 1 bis n'ait rien à traiter. Le tri se fait par un seul entier, et
       il est ORDONNÉ pour ça (voir `PbMode`) — le tir primaire, les projectiles
       ennemis et les six armes balistiques valent 0 et ne paient donc qu'une
       comparaison dans la boucle la plus chaude du jeu. */
    if (pb.mode[s]! >= PB_PERSISTENT) continue;
    const bx = pb.x[s]!;
    const by = pb.y[s]!;
    const br = pb.r[s]!;

    for (let j = en.count - 1; j >= 0; j--) {
      const e = en.at(j);

      // ⚠️ Un ennemi déjà à zéro PV ce tick n'absorbe plus rien. Sans ce test,
      // les projectiles suivants du même tick se consument sur un cadavre qui
      // ne sera libéré qu'au damageSystem : le surplus de dégâts est perdu et
      // une salve dense « rate » les ennemis vivants situés derrière.
      if (en.hp[e]! <= 0) continue;
      // Un ennemi encore en télégraphe est intouchable : sinon le joueur le
      // détruit avant de l'avoir vu et le télégraphe ne télégraphie plus rien.
      if (en.telegraph[e]! > 0) continue;
      /* Blindage par les parties : le noyau est intouchable tant qu'il lui
         reste une arme. C'est le motif « casser les armes avant le porteur »,
         et il change la nature du combat — le joueur choisit SON ORDRE de
         destruction, donc sa difficulté. Détruire les canons d'abord rend le
         combat long mais sûr ; viser le noyau n'est même pas permis. */
      if (en.shielded[e] === 1 && en.livingParts(e) > 0) continue;

      const rr = br + en.r[e]!;
      const d2 = sweptDist2(
        pb.px[s]!,
        pb.py[s]!,
        bx,
        by,
        en.px[e]!,
        en.py[e]!,
        en.x[e]!,
        en.y[e]!,
      );
      if (d2 > rr * rr) continue;

      /* ═══ Bouclier directionnel (PLAN.md §6.4, E08 ÉGIDE) ══════════════
         Le tir touche. Reste à savoir PAR OÙ.

         ── Convention d'axe ──
         Le cône protège la face tournée vers le joueur. Le joueur occupe le bas
         du terrain (y ≈ −380) et les ennemis descendent depuis le haut : la
         face exposée est donc celle des Y NÉGATIFS, et l'axe du cône est
         (0, −1). Écrire « vers le haut » en pensant au sens de déplacement des
         projectiles du joueur donnerait un bouclier tourné vers le vide, et un
         ennemi dont le bouclier ne sert à rien — panne muette, aucun test
         d'existence ne l'attraperait.

         ── On mesure une POSITION, pas une direction de vol ──
         Le côté touché est déduit du point d'impact relatif au centre de
         l'ennemi, pas du vecteur vitesse du projectile. C'est la seule des deux
         lectures que le joueur peut vérifier à l'œil : il voit un arc dessiné
         sur le ventre de l'Égide, et il voit son tir toucher soit l'arc, soit
         l'épaule. Trancher sur la vitesse rendrait le résultat indépendant de
         l'endroit visé — deux tirs frappant des points opposés du même ennemi
         auraient le même sort, ce qui est indéfendable devant un joueur.

         Conséquence chiffrée à connaître : avec une hurtbox de 55 u et un
         projectile de 14 u, le contact a lieu à 69 u du centre. Le seuil de
         55° s'y traduit par un décalage latéral de 56,7 u — l'épaule est donc
         une bande d'une douzaine d'unités de chaque côté. Étroit pour une gerbe
         mono-cible, gratuit pour une gerbe large : c'est exactement la
         discrimination par la FORME du build que le plan attend, et non par le
         volume de dégâts.

         ── Comparaison sans racine carrée ──
         « l'angle est sous le seuil » s'écrit  −dy ≥ cos(demi) × ‖d‖.
         Les deux membres étant positifs quand dy < 0, on élève au carré et la
         racine disparaît. */
      const sh = en.shieldHp[e]!;
      if (sh > 0) {
        const sdx = bx - en.x[e]!;
        const sdy = by - en.y[e]!;
        if (sdy < 0) {
          const c = cos(en.shieldHalf[e]!);
          if (sdy * sdy >= c * c * (sdx * sdx + sdy * sdy)) {
            const dmgS = pb.damage[s]!;
            const left = sh - dmgS;
            // Pas de report du surplus sur le corps. Un tir qui fait tomber le
            // bouclier est intégralement absorbé : sans quoi la dernière
            // touche vaudrait double et le seuil des 800 PV cesserait d'être
            // celui qui est affiché.
            en.shieldHp[e] = left > 0 ? left : 0;
            w.events.push(Ev.BulletBlocked, e, bx, by, dmgS);
            pb.free(s);
            break;
          }
        }
      }

      const dmg = pb.damage[s]!;
      en.hp[e] = en.hp[e]! - dmg;
      knockback(w, e, pb.vx[s]!, pb.vy[s]!, dmg);
      // `a` porte le RANG de l'ennemi touché (0/1/2), pas son slot : la
      // présentation sonorise l'impact sur les GROS ennemis sans avoir à relire
      // un slot qui, s'il a tué, est déjà libéré au moment de la consommation.
      w.events.push(Ev.EnemyDamaged, w.defs.enemies[en.kind[e]!]?.rank ?? 0, bx, by, dmg);
      pb.free(s);
      break; // un projectile ne touche qu'une cible (pas de perforation au J1)
    }
  }

  /* ═══ 1 bis. projectiles joueur PERSISTANTS → ennemis ════════════════════
     La passe 1 répond à « qui touche quoi », et se termine toujours par la mort
     du projectile. Celle-ci répond à la question que le catalogue a fini par
     poser : « et ensuite ? » Un disque continue et compte ses coupes, un arc
     repart vers un autre corps, un rayon reste et frappe de nouveau.

     ── Pourquoi une passe SÉPARÉE et non une branche dans la passe 1 ────────
     La passe 1 est la boucle la plus chaude du jeu — quelques centaines de tirs
     contre quelques centaines d'ennemis, à 120 Hz. Y greffer la comptabilité de
     la perforation, l'anti-répétition par uid et la cadence du rayon ferait
     payer à CHAQUE projectile primaire le coût d'un comportement que trois
     armes du catalogue utilisent. Séparées, les deux boucles ne se croisent
     jamais : un projectile est dans l'une ou dans l'autre, jamais dans les deux,
     et le tri coûte une comparaison d'entiers.

     ── Elle tourne avant la sortie « joueur mort » ──────────────────────────
     Volontairement, comme la passe 1 : un disque en vol au moment de la mort du
     joueur doit finir sa course. Le contraire ferait disparaître les dégâts
     d'un tir déjà payé, et le joueur l'attribuerait à sa mort — ce qui est une
     punition qu'il n'a pas méritée et qu'aucun message ne lui explique. */
  for (let i = pb.count - 1; i >= 0; i--) {
    const s = pb.at(i);
    const mode = pb.mode[s]!;
    if (mode < PB_PERSISTENT) continue;

    /* Cadence du rayon. C'est ce qui remplace la mort du projectile comme
       limiteur : un segment ancré chevauche sa cible pendant des dizaines de
       ticks, et sans cadence il infligerait ses dégâts 120 fois par seconde —
       soit une arme mille fois au-dessus de la primaire, et l'anneau
       d'événements saturé par une seule cible. */
    if (mode === PbMode.Beam) {
      const cd = pb.hitCd[s]!;
      if (cd > 0) {
        pb.hitCd[s] = cd - DT > 0 ? cd - DT : 0;
        continue;
      }
    }

    const bx = pb.x[s]!;
    const by = pb.y[s]!;
    const br = pb.r[s]!;

    for (let j = en.count - 1; j >= 0; j--) {
      const e = en.at(j);
      if (en.hp[e]! <= 0) continue;
      if (en.telegraph[e]! > 0) continue;
      if (en.shielded[e] === 1 && en.livingParts(e) > 0) continue;

      /* Anti-répétition. Un disque met plusieurs ticks à traverser un corps :
         sans cette exclusion il le blesserait à chaque tick de la traversée et
         « traverser quatre ennemis » vaudrait trente coups sur le premier.
         C'est un uid et non un slot, parce qu'un slot recyclé désignerait un
         ennemi arrivé depuis.

         Le rayon en est exempt : re-frapper la même cible est exactement ce
         qu'on lui demande, et c'est sa cadence qui le borne. */
      if (mode !== PbMode.Beam && en.uid[e]! === pb.lastUid[s]!) continue;

      const rr = br + en.r[e]!;
      const d2 = sweptDist2(
        pb.px[s]!,
        pb.py[s]!,
        bx,
        by,
        en.px[e]!,
        en.py[e]!,
        en.x[e]!,
        en.y[e]!,
      );
      if (d2 > rr * rr) continue;

      /* Bouclier directionnel — même convention d'axe et même comparaison sans
         racine que la passe 1, dont le raisonnement complet est écrit plus haut.
         Aucune arme ne contourne l'Égide : une perforation qui ignorerait le
         bouclier ferait de ces trois armes la réponse universelle à un ennemi
         conçu pour n'en avoir qu'une, celle de la FORME du tir. */
      const sh = en.shieldHp[e]!;
      if (sh > 0) {
        const sdx = bx - en.x[e]!;
        const sdy = by - en.y[e]!;
        if (sdy < 0) {
          const c = cos(en.shieldHalf[e]!);
          if (sdy * sdy >= c * c * (sdx * sdx + sdy * sdy)) {
            const dmgS = pb.damage[s]!;
            const left = sh - dmgS;
            en.shieldHp[e] = left > 0 ? left : 0;
            w.events.push(Ev.BulletBlocked, e, bx, by, dmgS);
            // Un disque et un arc s'écrasent sur le cône comme n'importe quel
            // tir. Un rayon, non : il n'est pas un objet qui vole, il est une
            // pression maintenue, et il use le bouclier impulsion après
            // impulsion.
            if (mode === PbMode.Beam) pb.hitCd[s] = BEAM_PULSE;
            else pb.free(s);
            break;
          }
        }
      }

      const dmg = pb.damage[s]!;
      en.hp[e] = en.hp[e]! - dmg;
      knockback(w, e, pb.vx[s]!, pb.vy[s]!, dmg);
      // `a` porte le RANG de l'ennemi touché (0/1/2), pas son slot : la
      // présentation sonorise l'impact sur les GROS ennemis sans avoir à relire
      // un slot qui, s'il a tué, est déjà libéré au moment de la consommation.
      w.events.push(Ev.EnemyDamaged, w.defs.enemies[en.kind[e]!]?.rank ?? 0, bx, by, dmg);

      if (mode === PbMode.Beam) {
        pb.hitCd[s] = BEAM_PULSE;
        break;
      }

      /* ── Décroissance multi-cible, réservée au tir PRIMAIRE ────────────────
         `dmg` ci-dessus a été lu AVANT cette ligne : le contact courant a donc
         porté à pleine puissance, et c'est le PROCHAIN ennemi de la file qui la
         verra décrue. On discrimine par famille : le fuselage et les drones
         (tir primaire perforant/ricochet) sont bornés, les secondaires — dont
         le budget de contacts et `validateSecondaries` font déjà le travail — ne
         le sont pas, sous peine de casser leurs tests. Lecture/écriture
         explicite plutôt qu'un `*=` indexé (règle de pureté de src/sim). */
      const fam = pb.fam[s]!;
      if (fam === Fam.Player || fam === Fam.PlayerDrone) {
        pb.damage[s] = pb.damage[s]! * PRIMARY_PIERCE_FALLOFF;
      }

      pb.lastUid[s] = en.uid[e]!;
      const rest = pb.hits[s]! - 1;
      pb.hits[s] = rest;
      if (rest <= 0) {
        pb.free(s);
        break;
      }

      /* Le SAUT. Le trait est replacé sur le corps qu'il vient de frapper, d'où
         `steerSecondaries` lui trouvera une autre cible au tick suivant. Sa
         position d'avant-tick est replacée avec lui : l'omettre laisserait un
         segment balayé long de tout l'écran, qui toucherait au passage tout ce
         qui se trouve entre le point de départ et le point d'arrivée du saut —
         un arc qui nettoie une diagonale entière, exactement ce que le budget de
         contacts existe pour empêcher. */
      if (mode === PbMode.Arc) {
        pb.x[s] = en.x[e]!;
        pb.y[s] = en.y[e]!;
        pb.px[s] = en.x[e]!;
        pb.py[s] = en.y[e]!;
      }
      break; // un contact par tick, comme en passe 1
    }
  }

  /* ═══ 1 ter. projectiles joueur → OBSTACLES ══════════════════════════════
     Un astéroïde BLOQUE les tirs : la balle s'y écrase, elle ne le traverse
     pas. Cette passe vient APRÈS les deux passes ennemies, et c'est un choix
     assumé : un tir vérifie d'abord les ennemis, puis les obstacles. À la
     densité visée (une poignée de rochers), une occlusion exacte « qui est
     devant » coûterait un tri inter-pools pour un cas de recouvrement rare — la
     même force brute que la passe 1 revendique. Un tir qui rate tout ennemi et
     rencontre un rocher s'y consume ; c'est le comportement attendu.

     ⚠️ Comme la passe 1, elle IGNORE les projectiles persistants (≥ PB_PERSISTENT)
     et elle tourne AVANT la sortie « joueur mort » : une balle déjà tirée doit
     finir sa course, un obstacle la bloque même à l'instant de la mort. Elle
     tourne aussi quand le pool d'obstacles est vide, où elle ne coûte que le
     test `ob.count` — le monde nu des tests de moteur n'en paie rien de plus. */
  const ob = w.obstacles;
  for (let i = pb.count - 1; i >= 0; i--) {
    const s = pb.at(i);
    if (pb.mode[s]! >= PB_PERSISTENT) continue;
    const bx = pb.x[s]!;
    const by = pb.y[s]!;
    const br = pb.r[s]!;

    for (let j = ob.count - 1; j >= 0; j--) {
      const o = ob.at(j);
      const rr = br + ob.r[o]!;
      const d2 = sweptDist2(
        pb.px[s]!,
        pb.py[s]!,
        bx,
        by,
        ob.px[o]!,
        ob.py[o]!,
        ob.x[o]!,
        ob.y[o]!,
      );
      if (d2 > rr * rr) continue;

      /* La balle est CONSOMMÉE dans tous les cas — c'est ce que « bloque »
         signifie. Sur un indestructible, elle disparaît sans entamer quoi que
         ce soit ; sur un destructible, elle retire ses PV et peut le briser. */
      if (ob.destructible[o] === 1) {
        const dmg = pb.damage[s]!;
        const left = ob.hp[o]! - dmg;
        if (left <= 0) {
          // Brisé CE tick, et libéré ICI plutôt que différé : `obstacleSystem`
          // tourne avant les collisions, donc un obstacle laissé à 0 PV resterait
          // collisionnable — et blesserait le joueur au contact — jusqu'au tick
          // suivant. On le retire tout de suite ; le slot déplacé par `free` a
          // déjà été visité (itération arrière).
          const obx = ob.x[o]!;
          const oby = ob.y[o]!;
          const obr = ob.r[o]!;
          w.events.push(Ev.ObstacleBroken, ob.type[o]!, obx, oby, obr);
          // Au-dessus d'une certaine taille, le rocher ne disparaît pas : il se
          // BRISE en fragments plus petits, de tailles différentes, qui s'écartent
          // de l'impact. Sous le seuil, `shatterObstacle` est un no-op. Les
          // fragments naissent APRÈS l'obstacle courant dans le pool ; l'itération
          // arrière ne les revisite pas ce tick-ci.
          shatterObstacle(w, obx, oby, obr);
          ob.free(o);
        } else {
          ob.hp[o] = left;
          w.events.push(Ev.ObstacleHit, o, bx, by, dmg);
        }
      } else {
        // Obstacle INDESTRUCTIBLE : le tir rebondit, un impact mais aucune casse.
        w.events.push(Ev.ObstacleHit, o, bx, by, 0);
      }
      pb.free(s);
      break; // un projectile ne bloque que sur un obstacle
    }
  }

  if (p.dead) return;
  const eb = w.eb;

  /* ═══ 2. graze ═══════════════════════════════════════════════════════════
     Passe SÉPARÉE, et en avant : elle ne libère rien.

     La séparer de la détection d'impact corrige un défaut discret. Les deux
     tests vivaient dans la même boucle et la mort déclenchait un `return`
     immédiat : toutes les balles pas encore visitées perdaient leur graze pour
     ce tick. Le joueur était donc puni deux fois — il mourait, et le frôlement
     qu'il venait de réussir n'était pas compté. Pire, ce qu'il gagnait
     dépendait de l'ordre interne du pool, invisible depuis le jeu et variable
     d'une partie à l'autre. */
  const grazeR2 = FEEL.grazeRadius * FEEL.grazeRadius;
  for (let i = 0; i < eb.count; i++) {
    const s = eb.at(i);
    // Comptabilisé une seule fois par balle : rester collé à un projectile
    // ferait sinon défiler le score tout seul.
    if (eb.grazed[s] !== 0) continue;
    const dx = eb.x[s]! - p.x;
    const dy = eb.y[s]! - p.y;
    if (dx * dx + dy * dy < grazeR2) {
      eb.grazed[s] = 1;
      /* ⚠️ En Recrue, le frôlement ne rapporte RIEN (PLAN.md §6.8). L'anneau
         reste visible et l'événement part quand même — on montre la mécanique.
         Mais un système qui récompense de raser les projectiles apprend à un
         débutant exactement le geste qui le tue ; l'inciter à le poursuivre
         avant qu'il en ait les moyens est la pire pédagogie possible.

         Le marqueur est posé dans tous les cas pour que la balle ne puisse pas
         rapporter deux fois si la difficulté changeait en cours de partie —
         elle ne le peut pas aujourd'hui, mais un invariant gratuit vaut mieux
         qu'une supposition. */
      if (GRAZE_SCORES[clampDifficulty(w.run.difficulty)]!) {
        w.grazeCount++;
        // La jauge d'ulti se nourrit aussi du risque pris : frôler la charge un peu.
        w.player.ulti = Math.min(1, w.player.ulti + ULTI_FILL_GRAZE);
        /* La bombe TEMPS MORT majore la VALEUR d'un frôlement, jamais son
           compte. La distinction n'est pas cosmétique : `grazeCount` alimente
           le jalon « 200 frôlements », qui mesure une maîtrise. Le majorer
           reviendrait à vendre un jalon de maîtrise à 700 crédits. */
        w.score += GRAZE_SCORE * grazeValueMul(p.bomb, p.bombFxT);
      }
      w.events.push(Ev.Graze, s, eb.x[s]!, eb.y[s]!, 0);
    }
  }

  /* ═══ 3. projectiles ennemis → joueur ════════════════════════════════════
     Une fenêtre de death-bomb déjà ouverte rend le joueur intouchable : sans
     ça, une seconde balle rouvrirait la fenêtre en boucle et la mort ne
     s'acterait jamais.

     ⚠️ `vulnerable` est un DRAPEAU, plus un `return` prématuré. La version
     précédente sortait de la fonction entière, ce qui était sans conséquence
     tant que le joueur était la dernière chose à tester. Depuis que l'escorte
     et les corps ennemis ont leurs propres passes, ce raccourci rendait les
     drones invulnérables pendant les 1,4 s d'immunité du joueur — l'immunité
     de l'un protégeait les autres, ce qui n'a aucun sens : les drones sont des
     appareils distincts, ils n'ont pas de vie à perdre et rien à devoir. */
  const vulnerable = p.iframes <= 0 && p.deathBombT <= 0;

  if (vulnerable) {
    for (let i = eb.count - 1; i >= 0; i--) {
      const s = eb.at(i);

      // La hitbox effective d'une balle vaut 85 % de son rayon visuel
      // (PLAN.md §4.4). C'est la seule assistance invisible tolérée, et elle rend
      // la lecture littéralement exacte : la zone dangereuse est le noyau blanc
      // que le joueur voit.
      const hr = FEEL.hitboxRadius + eb.r[s]! * 0.85;

      const d2 = sweptDist2(eb.px[s]!, eb.py[s]!, eb.x[s]!, eb.y[s]!, p.px, p.py, p.x, p.y);
      if (d2 < hr * hr) {
        eb.free(s);
        hitPlayer(w);
        return; // la mort purge de toute façon les balles alentour
      }
    }
  }

  /* ═══ 4. projectiles ennemis → ESCORTE ═══════════════════════════════════
     Les drones ont leur propre surface, et c'est une demande de jeu : « je dois
     protéger mon vaisseau ET mes drones ». Sans cela, l'escorte n'était qu'un
     bonus de débit sans contrepartie — plus on en avait, plus on tirait, sans
     que rien n'ait jamais à être défendu.

     ⚠️ Cette passe vient APRÈS l'impact sur le joueur, qui sort de la fonction
     dès qu'il touche. L'ordre inverse laisserait un drone intercepter un
     projectile déjà en route vers le point de hitbox — le joueur survivrait à
     une balle qu'il voit passer en plein centre, ce qui est illisible.

     ⚠️ Un drone touché ne coûte AUCUNE vie. Il sort du service quelques
     secondes, et c'est tout. L'inverse ferait des drones un handicap net : on
     achèterait une carte épique pour élargir sa propre zone de mort. */
  for (let i = 0; i < p.droneDown.length; i++) {
    if (p.droneDown[i]! > 0) continue;
    // Seuls les emplacements réellement occupés existent : au-delà du nombre
    // de projectiles acquis, il n'y a pas d'appareil à toucher.
    if (i >= w.progress.stats.bulletCount - HULL_GUNS) break;

    const d = DRONE_SLOTS[i]!;
    const dx0 = p.px + d.x;
    const dy0 = p.py + d.y;
    const dx1 = p.x + d.x;
    const dy1 = p.y + d.y;

    for (let k = eb.count - 1; k >= 0; k--) {
      const s = eb.at(k);
      const hr = DRONE_RADIUS + eb.r[s]! * 0.85;
      const d2 = sweptDist2(eb.px[s]!, eb.py[s]!, eb.x[s]!, eb.y[s]!, dx0, dy0, dx1, dy1);
      if (d2 < hr * hr) {
        p.droneDown[i] = DRONE_DOWN_TIME;
        // Le projectile est CONSOMMÉ. Le laisser poursuivre sa route après avoir
        // traversé un appareil visiblement solide contredirait ce que le joueur
        // voit — et la lisibilité prime sur la sévérité.
        w.events.push(Ev.BulletBlocked, s, eb.x[s]!, eb.y[s]!, 0);
        eb.free(s);
        break; // un drone déjà à terre ne peut plus être touché ce tick
      }
    }
  }

  /* ═══ 5. CORPS ennemis → joueur ══════════════════════════════════════════
     Un ennemi blesse au contact. Sans cette phase, un ennemi qui n'a pas
     d'émetteur ne peut RIEN faire : le Dard, dont toute la raison d'être est
     de déloger un joueur posé dans un coin, se réduisait à une trajectoire
     décorative. Et plus largement, traverser un vaisseau ennemi sans dommage
     contredit tout ce que le jeu montre par ailleurs.

     ── Le rayon employé est le VISUEL, jamais la hurtbox ──
     La hurtbox vaut 120 % du visuel (PLAN.md §6.2). Cette générosité est un
     CADEAU FAIT AU JOUEUR : elle rend ses tirs plus efficaces qu'ils n'en ont
     l'air. La retourner contre lui inverserait exactement le contrat — le
     même chiffre servirait à l'aider quand il tire et à le punir quand il
     approche, ce qu'aucun joueur ne peut deviner. On divise donc par 1,2 pour
     revenir au corps qu'il voit à l'écran.

     ── Balayé, comme les trois autres phases ──
     Un Dard pique à 720 u/s, soit 6 u par tick : un test ponctuel le ferait
     traverser le joueur sans contact une fois sur deux. `sweptDist2` prend en
     compte le déplacement des DEUX corps.

     ── Le télégraphe protège ──
     Un ennemi en cours d'apparition n'est ni touchable ni dangereux : il ne
     peut donc pas blesser au contact non plus. Sans cette exclusion, un ennemi
     apparaissant sur le joueur le tuerait avant d'être visible, ce qui est la
     mort la plus injuste que le genre sache produire. */
  if (!vulnerable) return;

  const enPhase4 = w.enemies;
  for (let i = enPhase4.count - 1; i >= 0; i--) {
    const s = enPhase4.at(i);
    if (enPhase4.telegraph[s]! > 0) continue;

    const cr = FEEL.hitboxRadius + enPhase4.r[s]! / 1.2;
    const d2 = sweptDist2(
      enPhase4.px[s]!,
      enPhase4.py[s]!,
      enPhase4.x[s]!,
      enPhase4.y[s]!,
      p.px,
      p.py,
      p.x,
      p.y,
    );
    if (d2 < cr * cr) {
      /* ⚠️ L'ennemi N'EST PAS détruit par le contact.

         Le faire mourir donnerait au joueur un moyen de nettoyer l'écran en
         encaissant des coups — dans un jeu où une vie vaut bien plus qu'un
         ennemi, ce serait immédiatement la stratégie optimale, et le contact
         cesserait d'être un danger pour devenir une ressource.

         Conséquence à connaître : le joueur ressort du corps ennemi encore
         dedans. C'est l'invulnérabilité post-impact qui l'en sort — elle dure
         assez longtemps pour traverser n'importe quel ennemi du bestiaire. */
      hitPlayer(w);
      return;
    }
  }

  /* ═══ 6. OBSTACLES → joueur ══════════════════════════════════════════════
     Un astéroïde qui percute le joueur le blesse, exactement comme un corps
     ennemi : on réutilise `hitPlayer`, donc les i-frames, la fenêtre de
     death-bomb et la purge des balles. Aucun chemin parallèle — un obstacle qui
     blesserait autrement serait une seconde règle de dégâts à maintenir.

     ── Le rayon employé ──
     Le rayon d'un obstacle EST son rayon de collision (pas de hurtbox gonflée à
     corriger comme pour l'ennemi) : `hitboxRadius + ob.r`, sans division.

     ── Balayé ──
     Un obstacle descend au défilement, mais une vitrine peut en lancer un bien
     plus rapide, et le déplacement du joueur s'ajoute. `sweptDist2` prend les
     deux segments : un rocher rapide ne traverse pas le joueur entre deux ticks.

     ── L'obstacle SURVIT au contact ──
     Comme l'ennemi : le détruire ferait du choc une ressource de nettoyage. Le
     joueur ressort du rocher encore dedans, et c'est l'invulnérabilité qui l'en
     sort — assez longue pour traverser n'importe quel obstacle du catalogue.

     Cette passe est protégée par le même `vulnerable` que les corps ennemis
     (testé plus haut) : un joueur en i-frames ou en fenêtre de death-bomb la
     traverse sans être touché. */
  const obPhase = w.obstacles;
  for (let i = obPhase.count - 1; i >= 0; i--) {
    const o = obPhase.at(i);
    const cr = FEEL.hitboxRadius + obPhase.r[o]!;
    const d2 = sweptDist2(
      obPhase.px[o]!,
      obPhase.py[o]!,
      obPhase.x[o]!,
      obPhase.y[o]!,
      p.px,
      p.py,
      p.x,
      p.y,
    );
    if (d2 < cr * cr) {
      hitPlayer(w);
      return;
    }
  }
}

function hitPlayer(w: World): void {
  const p = w.player;

  /* ═══ Bouclier joueur : il encaisse le coup À LA PLACE d'une vie ══════════
     Tant qu'il reste des charges, le coup CASSE le bouclier au lieu d'ouvrir la
     fenêtre de mort. On déduit une charge, on accorde de COURTES i-frames — assez
     pour ne pas tout perdre sur un amas dense ni rester collé dans un corps
     ennemi, pas assez pour tenir lieu d'invulnérabilité — et on RELANCE le délai
     de régénération : sous le feu, le bouclier ne se recharge pas.

     ⚠️ Le coup reste COMPTÉ dans `hitsTaken`, exactement comme un death-bomb : le
     bouclier a été touché, donc le joueur s'est mis en danger. Un jalon « sans
     être touché » doit le savoir (sinon le bouclier le vend), et le rang caché
     doit y voir un accroc.

     ⚠️ On ne PURGE PAS les balles alentour — ce serait faire du bouclier une
     mini-bombe qui nettoie l'écran à chaque absorption. Les i-frames suffisent à
     protéger l'instant qui suit. On réutilise `Ev.BulletBlocked` : « un projectile
     arrêté sans blesser », ce que la présentation dessine déjà pour l'absorption
     d'un drone ou d'une bombe — aucun nouvel événement à inventer.

     ⚠️ Placé AVANT tout le reste : dès qu'il absorbe, on sort. La fenêtre de
     death-bomb, le compteur de balles purgées, le retrait de vie — rien de tout
     cela ne doit se produire tant qu'une charge protège encore. */
  if (p.shield > 0) {
    const left = p.shield - SHIELD_HIT_COST;
    p.shield = left > 0 ? left : 0;
    if (p.iframes < SHIELD_BREAK_IFRAMES) p.iframes = SHIELD_BREAK_IFRAMES;
    p.shieldRegenCd = SHIELD_REGEN_DELAY;
    w.progress.hitsTaken++;
    w.events.push(Ev.ShieldBlocked, 0, p.x, p.y, p.shield);
    return;
  }

  /* ⚠️ L'impact n'acte PAS la mort — il ouvre la fenêtre de death-bomb
     (PLAN.md §6.3). Pendant 150 ms, le joueur continue de piloter et peut
     encore s'en sortir en bombant ; c'est `playerSystem` qui commet la mort si
     la fenêtre se referme sans rien.

     Le joueur garde le contrôle pendant ces 150 ms, et c'est indispensable :
     une fenêtre de rattrapage pendant laquelle on ne peut rien faire ne
     rattrape rien. */
  p.deathBombT = FEEL.deathBombWindow;
  // Compté À L'IMPACT, pas à la mort : un coup annulé par un death-bomb reste
  // un coup encaissé. Un jalon « sans être touché » doit le savoir, sinon il
  // récompense la maîtrise du rattrapage plutôt que celle de l'esquive.
  w.progress.hitsTaken++;

  w.events.push(Ev.PlayerHit, 0, p.x, p.y, 1);

  /* Purge des balles proches (PLAN.md §6.5). Sans elle, on réapparaît dans le
     rideau qui vient de nous tuer et on remeurt immédiatement — la frustration
     la plus citée du genre, et elle ne coûte que ces six lignes. */
  const eb = w.eb;
  const r2 = FEEL.deathClearRadius * FEEL.deathClearRadius;
  for (let i = eb.count - 1; i >= 0; i--) {
    const s = eb.at(i);
    const dx = eb.x[s]! - p.x;
    const dy = eb.y[s]! - p.y;
    if (dx * dx + dy * dy < r2) {
      w.events.push(Ev.BulletBlocked, s, eb.x[s]!, eb.y[s]!, 0);
      eb.free(s);
    }
  }
}
